import React, { useState } from "react";
import { MdMemory } from "react-icons/md";
import Card from "../card";
import { reindexRag, ReindexRagResult } from "../../services/copilot/copilotApi";
import { usePermissions } from "../../auth/PermissionProvider";
import { Permissions } from "../../auth/permissions";

/**
 * Ação de manutenção do RAG da NINA, restrita a `copilot.admin`: marca todo Project + TimeSheet para
 * reprocessamento pelo serviço de indexação. Serve para o backfill inicial de registros antigos.
 *
 * Vive em **Configurações**, não na home: é manutenção operacional pontual, não indicador — e a
 * tela inicial existe para decidir o que fazer hoje, não para acionar rotina de infraestrutura.
 *
 */
const CopilotAdminCard: React.FC = () => {
  const { can } = usePermissions();

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ReindexRagResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!can(Permissions.CopilotAdmin)) {
    return null;
  }

  const handleReindex = async () => {
    const confirmed = window.confirm(
      "Reindexar RAG marca todos os projetos e timesheets para reprocessamento. " +
        "Gera custo de embeddings na OpenAI. Continuar?"
    );
    if (!confirmed) return;

    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await reindexRag();
      setResult(data);
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          "Falha ao acionar a reindexação. Tente novamente."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card extra="w-full p-5">
      <div className="mb-1 flex items-center gap-2">
        <MdMemory className="h-5 w-5 text-dv-blue-500 dark:text-dv-cyan-300" aria-hidden="true" />
        <h3 className="text-lg font-bold text-navy-700 dark:text-white">
          NINA — Reindexar RAG
        </h3>
      </div>
      <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
        Marca todos os projetos e timesheets para reindexação semântica. O
        processamento ocorre em segundo plano (~100 por fonte a cada 15 min).
      </p>

      <button
        onClick={handleReindex}
        disabled={loading}
        className="mt-3 w-fit rounded-xl bg-ink-900 px-4 py-2 text-sm font-bold text-white shadow-dv-md transition-all hover:bg-ink-800 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-ink-900 dark:hover:bg-gray-100"
      >
        {loading ? "Acionando..." : "Reindexar RAG"}
      </button>

      {result && (
        <p className="mt-2 text-sm text-green-600">
          Reindexação acionada: {result.projectsMarked} projeto(s) e{" "}
          {result.timesheetsMarked} timesheet(s) marcados.
        </p>
      )}
      {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
    </Card>
  );
};

export default CopilotAdminCard;
